<template>
    <div class="mode-dropdown-row-wrapper" @click="emit('clicked', option.id)" :class="[appThemeMode]">
        <div class="content">
            <div v-if="option.id === 'light' || option.id === 'dark'" class="icon-box-wrapper" :class="option.id">
                <div class="icon-box">
                    <component class="icon" :is="modeIcon" />
                </div>
            </div>
            <div v-if="option.id === 'system'" class="automatic-theme-wrapper">
                <div class="icon-box-wrapper dark">
                    <div class="icon-box">
                        <component class="icon" :is="darkIcon"/>
                    </div>
                </div>
                <div class="icon-box-wrapper light">
                    <div class="icon-box">
                        <component class="icon" :is="lightIcon"/>
                    </div>
                </div>
            </div>
            <div class="text-wrapper">
                <div class="title" :class="{'selected': selectedOption === option.id}">{{ option.mode }}</div>
                <div class="description">{{ option.description }}</div>
            </div>
        </div>
        <!-- Check Icon -->
        <check-icon v-if="selectedOption === option.id" class="selected-icon"/>
    </div>
</template>

<script setup>
import CheckIcon from '@/assets/icons/check-line.svg?inline'

/* Vue */
import { computed, defineAsyncComponent } from 'vue'

/* Emmitters */
const emit = defineEmits(['clicked'])

/** Props
@appThemeMode {String}
@option { Object }
@selectedOption { String, Number }
*/
const props = defineProps({
    appThemeMode: String,
    option: Object,
    selectedOption: [String, Number]
})

const modeIcon = computed(() => {
	return defineAsyncComponent(() => import('@/assets/icons/' + props.option.icon + '.svg?inline'))
})

const lightIcon = computed(() => {
	return defineAsyncComponent(() => import('@/assets/icons/sun.svg?inline'))
})

const darkIcon = computed(() => {
	return defineAsyncComponent(() => import('@/assets/icons/moon.svg?inline'))
})
</script>

<style lang="scss" scoped>
.mode-dropdown-row-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;

    width: 100%;
    height: 72px;

    padding: 16px;

    cursor: pointer;
    border-bottom: 1px solid var(--secondary-light-2);

    &:first-of-type {
        border-top-right-radius: 8px;
        border-top-left-radius: 8px;
    }

    &:last-of-type {
        border-bottom: unset;
        border-bottom-right-radius: 8px;
        border-bottom-left-radius: 8px;
    }

    .content {
        display: flex;
        align-items: center;
        gap: 16px;

        .automatic-theme-wrapper {
            display: flex;
            align-items: flex-end;
            justify-content: flex-end;

            width: 56px;
            height: 40px;

            border-radius: 4px;
            overflow: hidden;

            .icon-box-wrapper {
                width: 50%;
                overflow: hidden;
                border-radius: 0;

                .icon-box {
                    width: calc(100% - 4px);
                    padding: 0 0 0 4px;
                }

                &.dark {
                    .icon-box {
                        border-bottom-right-radius: 0;
                    }
                }
            }
        }

        .icon-box-wrapper {
            display: flex;
            align-items: flex-end;
            justify-content: flex-end;

            width: 56px;
            height: 40px;

            border-radius: 4px;

            background-color: var(--secondary-light-3);

            .icon-box {
                display: flex;
                align-items: center;

                width: 47px;
                height: 31px;

                padding: 0 19px 0 12px;
                border-top-left-radius: 4px;
                border-bottom-right-radius: 4px;

                background-color: var(--secondary-light-4);
                border: 1px solid var(--secondary-light-3);

                .icon {
                    flex-shrink: 0;

                    :deep(.change-color-fill) {
                        fill: var(--secondary-dark-4);
                    }
                }
            }

            &.light {
                background-color: #F7F7F7;

                .icon-box {
                    background-color: #FFFFFF;
                    border: 1px solid #F7F7F7;

                    .icon {
                        :deep(.change-color-fill) {
                            fill: #242526;
                        }
                    }
                }
            }

            &.dark {
                background-color: #242526;

                .icon-box {
                    background-color: #3B3D40;
                    border: 1px solid #242526;

                    .icon {
                        :deep(.change-color-fill) {
                            fill: #DEDEDE;
                        }
                    }
                }
            }
        }

        .text-wrapper {
            display: flex;
            flex-direction: column;

            .title {
                @include H6-Medium;
                line-height: 18px;

                color: var(--secondary-dark-2);

                &.selected {
                    color: var(--primary)
                }
            }

            .description {
                @include H8-Regular;
                line-height: 13px;

                color: var(--secondary-dark-1);
            }
        }
    }

    @media (hover: hover) and (pointer: fine) {
        &:hover {
            background-color: var(--secondary-light-2);
        }
    }

    &:active {
        color: var(--primary);

        background-color: var(--secondary-light-1);
    }

    svg.selected-icon {
        width: 16px;
        height: 16px;

        pointer-events: none;

        :deep(.change-color-fill) {
            fill: var(--primary)
        }
    }

    &.dark {
        .content {
            .icon-box-wrapper {
                &.light {
                    background-color: #D9D9D9;

                    .icon-box {
                        background-color: #E8E8E8;

                        .icon {
                            :deep(.change-color-fill) {
                                fill: #363636;
                            }
                        }
                    }
                }

                &.dark {
                    background-color: #171717;

                    .icon-box {
                        background-color: #363636;

                        .icon {
                            :deep(.change-color-fill) {
                                fill: #D9D9D9;
                            }
                        }
                    }
                }
            }
        }
    }
}
</style>
